import { Suspense, useEffect, useState } from "react";
import { Outlet, useParams, NavLink } from "react-router";
import { useAtomValue } from "jotai";
import { ScrollText, BarChart3 } from "lucide-react";
import { RouteFallback } from "./RouteFallback";
import { localeAtom } from "../stores/locale";
import { t } from "../lib/i18n";
import { useGalaxyInit } from "../lib/hooks/useGalaxyInit";
import { useNavDrawer } from "../lib/hooks/useNavDrawer";
import GalaxySwitcher from "./GalaxySwitcher";
import { AccountMenu } from "./AccountMenu";
import DevMenu from "./DevMenu";
import { meApi, type AffiliationDetail } from "../api/client";
import { AceLogoMark } from "./AceLogoMark";

// AffiliationRecordContext is handed to the nested pages via <Outlet context>, so
// they don't re-fetch what the layout already loaded for its rail. User-scoped:
// the record is keyed off the JWT, so there is no :account here.
export interface AffiliationRecordContext {
  record: AffiliationDetail;
}

// ArchiveLayout is the chrome for a DEPARTED galaxy — a FROZEN, read-only space.
// It is NOT the operational Layout: a galaxy you've left can't be observed and
// its operational pages (observatory / modules / team) show OTHER members, so
// they never appear here. The rail mirrors the operational Sidebar — same brand
// header and the SAME GalaxySwitcher (it's archive-route-aware: the archived
// galaxy reads as the current selection, and the Active section is the way back)
// — but the nav is a small frozen one scoped to YOUR own record + cards.
// The record is user-scoped (/me/affiliations/:galaxyId): :galaxyId is the
// archived galaxy, and there is no :account — the data is keyed off the JWT.
export default function ArchiveLayout() {
  const { galaxyId } = useParams<{ galaxyId: string }>();
  // This route is a SIBLING of the operational Layout, so the galaxy enumeration
  // (the switcher's Active list) wouldn't otherwise run here. useGalaxyInit is
  // user-scoped (JWT memberships), so it's safe — it never reads the archived
  // galaxy operationally.
  useGalaxyInit();
  const [record, setRecord] = useState<AffiliationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Mobile drawer state — mirrors the operational Layout so the archived-galaxy
  // chrome collapses to a hamburger below lg too (the rail is fixed w-64 and
  // would otherwise eat the viewport on phones with no way to dismiss it).
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useNavDrawer();
  const locale = useAtomValue(localeAtom);

  useEffect(() => {
    const id = Number(galaxyId);
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    meApi
      .affiliation(id)
      .then(setRecord)
      .catch((e) => {
        console.error("affiliation record failed", e);
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [galaxyId]);

  const navItem = (
    to: string,
    end: boolean,
    Icon: typeof ScrollText,
    label: string,
  ) => (
    <NavLink
      to={to}
      end={end}
      onClick={() => setSidebarOpen(false)}
      className="flex items-center gap-md rounded-lg px-md py-sm text-sm transition-colors"
      style={({ isActive }) =>
        isActive
          ? {
              background: "var(--bg-card)",
              color: "var(--text-strong)",
              border: "1px solid var(--border)",
            }
          : { color: "var(--text-muted)" }
      }
    >
      <Icon size={15} />
      {label}
    </NavLink>
  );

  return (
    <div
      className="flex h-screen"
      style={{ backgroundColor: "var(--bg-void)" }}
    >
      <div className="starfield" aria-hidden="true" />

      {/* Mobile overlay — tap to dismiss the drawer below lg. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* The rail is an off-canvas drawer below lg (hamburger-toggled) and a
          static sidebar at lg+ — mirrors the operational Layout so the archived
          galaxy chrome collapses the same way instead of pinning a w-64 rail. */}
      <aside
        className={`surface-dark fixed inset-y-0 left-0 z-30 flex h-full w-64 transform flex-col transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ borderRight: "1px solid var(--border)" }}
      >
        {/* Brand header — identical to the operational Sidebar so the two chromes
            read as one product. */}
        <div className="flex items-center gap-md p-lg">
          <AceLogoMark glow="chrome" className="h-10 w-10" />
          <h1
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.125rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text-strong)",
            }}
          >
            OrbitLens{" "}
            <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
          </h1>
        </div>

        {/* The SAME switcher the operational sidebar uses — archive-route-aware. */}
        <GalaxySwitcher />

        {/* Frozen nav — only YOUR own record + cards (no operational pages). */}
        <nav className="flex flex-col gap-xs px-md pt-sm">
          {navItem(
            `/me/affiliations/${galaxyId}`,
            true,
            ScrollText,
            t("archiveLayout.record", locale),
          )}
          {navItem(
            `/me/affiliations/${galaxyId}/cards`,
            false,
            BarChart3,
            t("archiveLayout.cards", locale),
          )}
        </nav>

        {/* SAME bottom-left account menu the operational Sidebar uses (avatar
            popover + JP/EN + theme). mt-auto pins it to the bottom of the
            flex-col aside so the archived record view keeps the identity
            controls instead of dropping them. */}
        <div className="mt-auto">
          <AccountMenu />
        </div>
      </aside>

      <main className="relative z-10 flex-1 overflow-auto">
        {/* Mobile header with hamburger — lg:hidden, mirrors the operational Layout. */}
        <div
          className="flex items-center gap-md border-b p-lg lg:hidden"
          style={{ borderColor: "var(--border)" }}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t("archiveLayout.openMenu", locale)}
            className="transition-colors"
            style={{ color: "var(--text-muted)" }}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <span
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.0625rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text-strong)",
            }}
          >
            OrbitLens{" "}
            <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
          </span>
        </div>

        <div className="p-lg sm:p-2xl lg:p-3xl">
          {loading ? null : notFound || !record ? (
            <div
              className="mx-auto max-w-[48rem] rounded-xl p-3xl text-center text-sm"
              style={{
                border: "1px dashed var(--border)",
                color: "var(--text-dim)",
              }}
            >
              {t("archiveLayout.noRecord", locale)}
            </div>
          ) : (
            // Localized route-level Suspense for the lazy() archive pages — the
            // frozen rail stays painted while a page chunk loads (manifest §3).
            <Suspense fallback={<RouteFallback />}>
              <Outlet context={{ record } satisfies AffiliationRecordContext} />
            </Suspense>
          )}
        </div>
      </main>

      {/* Developer tools — same floating DEV button as the operational Layout.
          Renders only when the backend confirms dev tools are on (non-prod);
          no-op in prod (the endpoint 404s). The archive Dev tools (archive /
          restore an affiliation) are reached from here. */}
      <DevMenu />
    </div>
  );
}
